<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <OrganisationsTable
        :rows="organisations"
        :loading="isLoading || isDeletingOrganisation"
        :page="currentPage"
        :searchEnabled="true"
        :searchQuery="searchQuery"
        :key="actionsCompleted"
        :has-more-items="hasNextPage"
        :removable="true"
        :editable="true"
        @load="fetchNextPage"
        @search="search($event)"
        @create="showCreateOrganisationModal = true"
        @remove="
          (row) => {
            activeOrganisation = row;
            showDeleteOrganisationModal = true;
          }
        "
        @edit="
          (row) => {
            activeOrganisation = row;
            showUpdateOrganisationModal = true;
          }
        "
        @click-row="onClickOrganisationRow"
      />
    </div>
  </InfusePageLayout>

  <!-- CREATE -->
  <MutationOrganisationModal
    v-if="showCreateOrganisationModal"
    @close="showCreateOrganisationModal = false"
    @mutate="onCreateOrganisation"
    :loading="isCreatingOrganisation"
  />

  <!-- UPDATE -->
  <MutationOrganisationModal
    v-if="showUpdateOrganisationModal && activeOrganisation"
    :organisation="activeOrganisation"
    :loading="isUpdatingOrganisation"
    @close="closeUpdateModal"
    @mutate="onUpdateOrganisation"
  />

  <InfuseConfirmModal
    v-if="showDeleteOrganisationModal"
    :action-title="t('iam.organisations.header.delete_organisation')"
    :action-description="
      t('iam.organisations.info.delete_organisation', { organisation: activeOrganisation?.name || '' })
    "
    verification-text="delete"
    @confirm="onDeleteOrganisation"
    @close="closeDeleteModal"
  />
</template>

<script setup lang="ts">
import { InfusePageLayout } from '@infuse/ui/layouts';
import { ref, watch } from 'vue';
import { useOrganisations } from '@infuse/iam/organisation/queries';
import {
  useCreateOrganisation,
  useDeleteOrganisation,
  useUpdateOrganisation
} from '@infuse/iam/organisation/mutations';
import { CreateOrganisation, UpdateOrganisation, Organisation } from '@infuse/api/iam/organisation';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { MutationOrganisationModal, OrganisationsTable } from '@infuse/iam/organisation/ui';
import { useRouter } from 'vue-router';

const { t } = useI18n();

defineEmits(['reloadModel']);

const activeOrganisation = ref<Organisation | undefined>(undefined);

const { organisations, currentPage, isLoading, hasNextPage, fetchNextPage, searchQuery } = useOrganisations();
const { mutateAsync: createOrganisation, isPending: isCreatingOrganisation } = useCreateOrganisation();
const { mutateAsync: updateOrganisation, isPending: isUpdatingOrganisation } = useUpdateOrganisation();
const { mutateAsync: deleteOrganisation, isPending: isDeletingOrganisation } = useDeleteOrganisation();

const actionsCompleted = ref(0);
const showCreateOrganisationModal = ref(false);
const showUpdateOrganisationModal = ref(false);
const showDeleteOrganisationModal = ref(false);

const router = useRouter();

watch(
  () => searchQuery.value,
  (search) => {
    searchQuery.value = search;
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
}

async function onCreateOrganisation(vars: { organisation: CreateOrganisation }): Promise<void> {
  await createOrganisation({ organisation: vars.organisation });
  showCreateOrganisationModal.value = false;
}

async function onDeleteOrganisation(): Promise<void> {
  if (!activeOrganisation.value) {
    return;
  }
  await deleteOrganisation({ id: activeOrganisation.value.id });
  closeDeleteModal();
}

function closeDeleteModal() {
  showDeleteOrganisationModal.value = false;
  activeOrganisation.value = undefined;
}

async function onUpdateOrganisation(vars: { id: string; organisation: UpdateOrganisation }): Promise<void> {
  await updateOrganisation(vars);
  closeUpdateModal();
}

function closeUpdateModal() {
  showUpdateOrganisationModal.value = false;
  activeOrganisation.value = undefined;
}

function onClickOrganisationRow(row: Organisation) {
  router.push({ name: 'organisation.details', params: { id: row.id } });
}
</script>
